import { useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Empty,
	Flex,
	Modal,
	Segmented,
	Spin,
	Tag,
	Tooltip,
	Typography,
} from 'antd';
import { DiffEditor } from '@monaco-editor/react';
import dayjs from 'dayjs';

import {
	VSCPartData,
	VSCPartDataHistoryRecord,
	VSCPartJson,
} from '../../../interfaces/VersaBuilt.Interface';
import { useGetVSCPartDataHistoryQuery } from '../../../redux/api/versabuilt';
import { extractApiError } from '../../../utils/apiError';
import UnitSwitch from './UnitSwitch';
import { jsonToInches } from './units';

type CompareTarget = 'previous' | 'current';

const HISTORY_TYPE_LABELS: Record<VSCPartDataHistoryRecord['history_type'], string> = {
	'+': 'Created',
	'~': 'Changed',
	'-': 'Deleted',
};

const formatDate = (date: string) => dayjs(date).format('MMM D YYYY, h:mm A');

/** Top-level keys plus `parameters.*`, which is where nearly every edit lands. */
const getChangedKeys = (before: VSCPartJson | undefined, after: VSCPartJson) => {
	if (!before) {
		return [];
	}
	const changed: string[] = [];
	const compare = (
		a: Record<string, unknown>,
		b: Record<string, unknown>,
		prefix: string
	) => {
		for (const key of new Set([...Object.keys(a), ...Object.keys(b)])) {
			if (prefix === '' && key === 'parameters') {
				continue;
			}
			if (JSON.stringify(a[key]) !== JSON.stringify(b[key])) {
				changed.push(`${prefix}${key}`);
			}
		}
	};
	compare(before, after, '');
	compare(before.parameters ?? {}, after.parameters ?? {}, 'parameters.');
	return changed;
};

interface HistoryContentProps {
	program: VSCPartData;
	useInches: boolean;
	onChangeUnits: (useInches: boolean) => void;
}

const HistoryContent = ({
	program,
	useInches,
	onChangeUnits,
}: HistoryContentProps) => {
	const { data: records, isLoading, error } = useGetVSCPartDataHistoryQuery(
		program.id,
		{ refetchOnMountOrArgChange: true }
	);
	const [selectedId, setSelectedId] = useState<number | null>(null);
	const [compareTo, setCompareTo] = useState<CompareTarget>('previous');

	useEffect(() => {
		if (records?.length && selectedId === null) {
			setSelectedId(records[0].history_id);
		}
	}, [records, selectedId]);

	const rows = useMemo(
		() =>
			(records ?? []).map((record, index) => ({
				...record,
				previous: records?.[index + 1],
				changedKeys: getChangedKeys(
					records?.[index + 1]?.json_data,
					record.json_data
				),
			})),
		[records]
	);

	if (isLoading) {
		return (
			<Flex justify="center" className="py-10">
				<Spin />
			</Flex>
		);
	}
	if (error) {
		return <Alert type="error" showIcon message={extractApiError(error)} />;
	}
	if (!rows.length) {
		return <Empty description="No history recorded for this program" />;
	}

	const selected = rows.find((row) => row.history_id === selectedId) ?? rows[0];
	const current = rows[0];
	const [base, target] =
		compareTo === 'previous'
			? [selected.previous, selected]
			: [selected, current];

	const display = (json?: VSCPartJson) =>
		json ? JSON.stringify(useInches ? jsonToInches(json) : json, null, 2) : '';

	const describe = (record?: VSCPartDataHistoryRecord) =>
		record ? formatDate(record.history_date) : 'Nothing (first version)';

	return (
		<Flex gap="middle" align="stretch">
			<Flex
				vertical
				gap="small"
				className="overflow-y-auto pr-1"
				style={{ width: 320, flexShrink: 0, maxHeight: 'calc(65vh + 64px)' }}
			>
				{rows.map((row) => {
					const isSelected = row.history_id === selected.history_id;
					const changeCount = row.changedKeys.length;
					return (
						<button
							key={row.history_id}
							type="button"
							onClick={() => setSelectedId(row.history_id)}
							className={`w-full text-left rounded-md border px-3 py-2 transition-colors cursor-pointer ${
								isSelected
									? 'border-[#1677ff] bg-[#e6f4ff]'
									: 'border-[#d9d9d9] bg-white hover:border-[#4096ff] active:border-[#0958d9]'
							}`}
						>
							<Flex justify="space-between" align="center" gap="small">
								<Tag
									color={row.history_type === '+' ? 'green' : 'blue'}
									className="m-0"
								>
									{row.history_change_reason ??
										HISTORY_TYPE_LABELS[row.history_type]}
								</Tag>
								{row.previous && (
									<Tooltip
										title={
											changeCount
												? row.changedKeys.join(', ')
												: 'Identical to the previous version'
										}
									>
										<Typography.Text type="secondary" className="text-xs">
											{changeCount} {changeCount === 1 ? 'change' : 'changes'}
										</Typography.Text>
									</Tooltip>
								)}
							</Flex>
							<div className="mt-2 font-medium">
								{formatDate(row.history_date)}
							</div>
							<Typography.Text type="secondary" className="text-xs">
								{row.history_user?.full_name || row.history_user?.email || '—'}
								{' · '}
								{row.json_data?.op_count ?? '—'} setups
							</Typography.Text>
						</button>
					);
				})}
			</Flex>
			<Flex vertical gap="small" className="flex-grow min-w-0">
				<Flex justify="space-between" align="center" wrap="wrap" gap="small">
					<Segmented<CompareTarget>
						value={compareTo}
						onChange={setCompareTo}
						options={[
							{ label: 'Compare with previous', value: 'previous' },
							{ label: 'Compare with current', value: 'current' },
						]}
					/>
					<UnitSwitch useInches={useInches} onChange={onChangeUnits} />
				</Flex>
				<Flex justify="space-between">
					<Typography.Text type="secondary">{describe(base)}</Typography.Text>
					<Typography.Text type="secondary">
						{describe(target)}
						{target === current ? ' (current)' : ''}
					</Typography.Text>
				</Flex>
				<div className="border">
					<DiffEditor
						height="65vh"
						language="json"
						original={display(base?.json_data)}
						modified={display(target?.json_data)}
						options={{
							readOnly: true,
							originalEditable: false,
							minimap: { enabled: false },
							scrollBeyondLastLine: false,
							automaticLayout: true,
						}}
					/>
				</div>
			</Flex>
		</Flex>
	);
};

interface RobotProgramHistoryModalProps {
	program: VSCPartData | null;
	useInches: boolean;
	onChangeUnits: (useInches: boolean) => void;
	onClose: () => void;
}

const RobotProgramHistoryModal = ({
	program,
	useInches,
	onChangeUnits,
	onClose,
}: RobotProgramHistoryModalProps) => (
	<Modal
		title={`Change History${program?.json_data?.name ? ` — ${program.json_data.name}` : ''
			}`}
		open={!!program}
		onCancel={onClose}
		footer={null}
		destroyOnClose
		width="90vw"
		style={{ maxWidth: 1600 }}
		centered
	>
		{program && (
			<HistoryContent
				program={program}
				useInches={useInches}
				onChangeUnits={onChangeUnits}
			/>
		)}
	</Modal>
);

export default RobotProgramHistoryModal;
